/* eslint-disable max-lines */
import { Group, Line, Text } from "react-konva";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { useCurrentComposition } from "@design-lab/entities/composition";
import type { ShirtData, Surface } from "../model/types";
import { NecklineDivider } from "./NecklineDivider";
import { NecklineMeasurement } from "./NecklineMeasurement";
import { zoomAtom } from "../model/state";
import { ADULT_MEASUREMENTS, YOUTH_MEASUREMENTS } from "../lib/measurements";

export type NecklineProps = {
  placement: PlacementFormPosition;
  shirtData: ShirtData;
  surface: Surface;
  /** Surface data for the front placement. Used as a reference for some
   *  placements (e.g. frontLeftChest) where the main neckline guides should be
   *  positioned relative to the front neckline rather than the placement's own
   *  offsets.
   */
  frontSurface?: Surface | undefined;
  isSelectedColorLight: boolean;
};

export function Neckline({
  shirtData,
  surface,
  frontSurface,
  isSelectedColorLight,
  placement
}: NecklineProps) {
  const { isDesktop } = useResponsive();
  const zoom = useAtomValue(zoomAtom);
  const { currentProduct } = useCurrentComposition();
  const measurements = surface.isYouth
    ? YOUTH_MEASUREMENTS[placement]
    : ADULT_MEASUREMENTS[placement];

  const elements = useMemo(() => {
    if (!shirtData) return null;

    const { scale, offsetX, offsetY } = shirtData;

    // Choose which surface offsets should be used for the main neckline divider.
    // For the LEFT-CHEST placement we take the offsets from the FRONT placement –
    // this matches the Vue logic where the vertical ruler is anchored to the
    // front neckline while the dashed guide uses the left-chest offsets.
    const pivotSurfaceOffset =
      placement === "frontLeftChest" && frontSurface
        ? frontSurface.surfaceLocationOffset
        : surface.surfaceLocationOffset;

    const { topX: pivotTopX, topY: pivotTopY, bottomY: pivotBottomY } = pivotSurfaceOffset;

    const { topX, topY, bottomY } = surface.surfaceLocationOffset;

    const dividerLeft = pivotTopX * scale + offsetX;
    const dividerTop = pivotTopY * scale + offsetY;
    const dividerHeight = (pivotBottomY - pivotTopY) * scale - 4 / zoom;
    const measurementY = dividerTop + dividerHeight / 2;

    // Keep horizontal divider lines a constant visual length regardless of zoom
    const horizontalWidth = 20 / zoom;
    const halfWidth = horizontalWidth / 2;

    // Horizontal offsets for measurement text and size labels (screen-independent)
    const firstTextOffset = -60 / zoom;
    const firstSizeOffset = -62 / zoom;

    const secondTextOffset = 20 / zoom;
    const secondSizeOffset = 22 / zoom;

    const strokeColor = isSelectedColorLight ? "#000000" : "#FFFFFF";
    const backgroundFill = isSelectedColorLight ? "#000000" : "#FFFFFF";
    const backgroundTextFill = isSelectedColorLight ? "#FFFFFF" : "#000000";
    const plainTextFill = isSelectedColorLight ? "#000000" : "#FFFFFF"; // invert

    // Additional data required only for the FRONT_LEFT_CHEST placement – the dashed
    // guide that indicates the far edge of the collar.

    let chestGuide:
      | undefined
      | {
          left: number;
          top: number;
          height: number;
          descriptionX: number;
          descriptionY: number;
          printableAreaBottom: number;
          printableAreaCenterX: number;
        };

    if (placement === "frontLeftChest") {
      const chestLeft = topX * scale + offsetX;
      const chestTop = topY * scale + offsetY;
      const chestHeight = (bottomY - topY) * scale - 4 / zoom;

      const descriptionOffsetX = 10 / zoom;

      // Calculate printable area bottom position
      const printableAreaTop = surface.boundingBox.top * scale + offsetY;
      const printableAreaHeight = surface.boundingBox.height * scale;
      const printableAreaBottom = printableAreaTop + printableAreaHeight;

      // Calculate printable area center X position
      const printableAreaLeft = surface.boundingBox.left * scale + offsetX;
      const printableAreaWidth = surface.boundingBox.width * scale;
      const printableAreaCenterX = printableAreaLeft + printableAreaWidth / 2;

      chestGuide = {
        left: chestLeft,
        top: chestTop,
        height: chestHeight,
        descriptionX: chestLeft + descriptionOffsetX,
        descriptionY: chestTop + chestHeight / 2,
        printableAreaBottom,
        printableAreaCenterX
      };
    }

    return {
      dividerLeft,
      dividerTop,
      dividerHeight,
      horizontalWidth,
      halfWidth,
      strokeColor,
      backgroundFill,
      backgroundTextFill,
      plainTextFill,
      firstTextOffset,
      firstSizeOffset,
      secondTextOffset,
      secondSizeOffset,
      measurementY,
      chestGuide
    };
  }, [shirtData, surface, frontSurface, isSelectedColorLight, zoom, placement]);

  if (!elements) return null;

  const {
    dividerLeft,
    dividerTop,
    dividerHeight,
    horizontalWidth,
    halfWidth,
    strokeColor,
    backgroundFill,
    backgroundTextFill,
    plainTextFill,
    firstTextOffset,
    firstSizeOffset,
    secondTextOffset,
    secondSizeOffset,
    measurementY,
    chestGuide
  } = elements;

  // Elements now render directly in canvas coordinates and scale naturally with the Stage zoom.

  const centerX = dividerLeft;
  const dividerTopScaled = dividerTop;
  const dividerHeightScaled = dividerHeight;
  const frontLeftChestDescriptionOffsetX = isDesktop ? 10 : 28 / zoom ** 1.6 - 3;
  const frontLeftChestDescriptionOffsetY = (isDesktop ? -10 : -20) / zoom;
  const frontLeftChestWidth = isDesktop ? 120 / zoom : Math.max(40, Math.min(55, 100 / zoom));
  const isFrontSupported = currentProduct?.surfaceMeta?.sides?.front?.includes("front") ?? false;
  const showNecklineGuide = isFrontSupported || placement === "back";

  return (
    <Group listening={false}>
      {showNecklineGuide && (
        <>
          <NecklineDivider
            centerX={centerX}
            top={dividerTopScaled}
            height={dividerHeightScaled}
            halfWidth={halfWidth}
            horizontalWidth={horizontalWidth}
            strokeColor={strokeColor}
          />

          <NecklineMeasurement
            centerX={centerX}
            necklineY={measurementY}
            textOffset={firstTextOffset}
            sizeOffset={firstSizeOffset}
            measurementText={measurements.legendLeft.title}
            sizeLabel={measurements.legendLeft.description}
            plainTextFill={plainTextFill}
            backgroundFill={backgroundFill}
            backgroundTextFill={backgroundTextFill}
          />

          {measurements.legendRight && (
            <NecklineMeasurement
              centerX={centerX}
              necklineY={measurementY}
              textOffset={secondTextOffset}
              sizeOffset={secondSizeOffset}
              measurementText={measurements.legendRight.title}
              sizeLabel={measurements.legendRight.description}
              plainTextFill={plainTextFill}
              backgroundFill={backgroundFill}
              backgroundTextFill={backgroundTextFill}
            />
          )}
        </>
      )}

      {/* Dashed guide for the FRONT_LEFT_CHEST placement */}
      {placement === "frontLeftChest" && chestGuide && (
        <>
          <Line
            x={chestGuide.left}
            y={chestGuide.top}
            points={[0, 0, 0, chestGuide.height]}
            stroke={strokeColor}
            strokeWidth={2 / zoom}
            dash={[4 / zoom, 4 / zoom]}
            listening={false}
          />
          <Text
            x={
              isDesktop
                ? chestGuide.descriptionX + frontLeftChestDescriptionOffsetX
                : chestGuide.printableAreaCenterX - frontLeftChestWidth / (2 * zoom) + 3 // Offset of 3 pixels adjusts visual centering relative to PrintableArea on mobile
            }
            y={
              isDesktop
                ? chestGuide.descriptionY + frontLeftChestDescriptionOffsetY
                : chestGuide.printableAreaBottom + 20 / zoom // 8px padding top from PrintableArea bottom on mobile
            }
            text="centered over far edge of collar"
            fontSize={isDesktop ? 12 : 10}
            fill={plainTextFill}
            width={frontLeftChestWidth}
            scaleX={1 / zoom}
            scaleY={1 / zoom}
            listening={false}
          />
        </>
      )}
    </Group>
  );
}
